import { useRef, useState, useCallback } from "react";
import "@/App.css";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Instagram, Sparkles, Heart, Crown, Gift, Plane, UtensilsCrossed, MapPin, DollarSign, Music, Users, Search, Camera, Youtube, Twitter } from "lucide-react";
import { Scene3D } from "./components/Scene3D";
import { AcidTabAnimation } from "./components/AcidAnimation";
import { Button } from "./components/ui/button";
import IntroPage from "./components/IntroPage";
import PortalTransition from "./components/PortalTransition";
import { CheckoutModal, TrackOrderModal } from "./components/Checkout";
import CoachingSection from "./components/CoachingSection";
import AdminPanel from "./components/AdminPanel";
import AskAcidRaniSection from "./components/AskAcidRaniSection";

const INSTAGRAM_URL = "https://www.instagram.com/acidrani?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==";
const PATREON_URL = "https://patreon.com/acidrani1?utm_medium=unknown&utm_source=join_link&utm_campaign=creatorshare_fan&utm_content=copyLink";
const YOUTUBE_URL = "https://youtube.com/@acidrani?si=Jyl-8AxSG7VIxqZI";
const X_URL = "https://x.com/acidrani?s=21";
const PAYPAL_EMAIL = "tanyakhaleel@gmail.com";

const getPayPalLink = (amount, itemName) =>
  `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=${encodeURIComponent(PAYPAL_EMAIL)}&amount=${amount}&currency_code=USD&item_name=${encodeURIComponent(itemName)}&no_shipping=1`;

const getPayPalLinkINR = (amount, itemName) =>
  `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=${encodeURIComponent(PAYPAL_EMAIL)}&amount=${amount}&currency_code=INR&item_name=${encodeURIComponent(itemName)}&no_shipping=0`;

// Noise overlay component
const NoiseOverlay = () => <div className="noise-overlay" aria-hidden="true" />;

// Hero Section
const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  return (
    <section ref={ref} className="hero-section" data-testid="hero-section">
      <motion.div 
        className="relative z-10 text-center px-6"
        style={{ y, opacity }}
      >
        {/* Tagline */}
        <motion.p 
          className="font-handwritten text-[#CCFF00] text-lg md:text-xl mb-4 tracking-wide"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Unhinged • Sovereign • Electric
        </motion.p>

        {/* Main Title */}
        <motion.h1 
          className="text-hero font-heading text-white uppercase tracking-tight"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          style={{
            textShadow: '0 0 60px rgba(255, 0, 255, 0.5)',
          }}
        >
          <span className="text-gradient-pink">ACID</span>
          <br />
          <span className="text-white">RANI</span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p 
          className="mt-8 text-lg md:text-xl text-[#FFD1DC] max-w-xl mx-auto font-body"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          Sex & Dating Coach • Content Creator • Artist
        </motion.p>

        {/* CTA Buttons */}
        <motion.div 
          className="mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          <a 
            href={PATREON_URL} 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn-neon"
            data-testid="hero-patreon-btn"
          >
            <span className="flex items-center gap-2">
              <Crown size={20} />
              Join Patreon
            </span>
          </a>
          <a 
            href={INSTAGRAM_URL} 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn-outline"
            data-testid="hero-instagram-btn"
          >
            <span className="flex items-center gap-2">
              <Instagram size={20} />
              Follow Me
            </span>
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <motion.div 
            className="w-1 h-2 bg-[#FF00FF] rounded-full"
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        </div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section className="section-padding relative overflow-hidden" data-testid="about-section">
      <div className="max-w-6xl mx-auto">
        <motion.div 
          className="grid md:grid-cols-2 gap-12 items-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          {/* Image/Visual */}
          <motion.div 
            className="relative"
            initial={{ x: -50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative aspect-square max-w-md mx-auto">
              <div 
                className="absolute inset-0 rounded-3xl organic-shape"
                style={{
                  background: 'linear-gradient(135deg, #FF00FF 0%, #8B008B 50%, #FF1493 100%)',
                  filter: 'blur(40px)',
                  opacity: 0.5,
                }}
              />
              <div 
                className="relative glass-card w-full h-full rounded-3xl p-8 flex items-center justify-center"
              >
                <div className="text-center">
                  <motion.div
                    animate={{ rotate: [0, 360] }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="mb-6"
                  >
                    <Sparkles className="w-16 h-16 text-[#FF00FF] mx-auto" />
                  </motion.div>
                  <p className="font-handwritten text-2xl text-[#CCFF00]">
                    "Claim your body as sovereign territory"
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div 
            className="space-y-6"
            initial={{ x: 50, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="text-section font-heading text-white">
              Who is <span className="text-gradient-pink">Acid Rani</span>?
            </h2>
            <p className="text-[#FFD1DC] text-lg leading-relaxed">
              A seeker shaped by Canadian roots, American influences, and Indian chaos. 
              I'm here to dismantle sex taboos through honest teaching and raw art.
            </p>
            <p className="text-white/70 leading-relaxed">
              Sex-positive. Body-positive. Uninhibited. Through my content, coaching, 
              and art, I help you embrace your authentic self and explore the boundaries 
              of pleasure and liberation.
            </p>
            
            <div className="flex flex-wrap gap-3 pt-4">
              {['Sex-Positive', 'Feminist', 'Artist', 'Coach', 'Hedonist'].map((tag, i) => (
                <motion.span 
                  key={tag}
                  className="px-4 py-2 glass rounded-full text-sm text-[#FFD1DC] border border-white/10"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ 
                    borderColor: '#FF00FF',
                    scale: 1.05,
                  }}
                >
                  {tag}
                </motion.span>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Patreon Section
const PatreonSection = () => {
  const [checkout, setCheckout] = useState(null);

  const tiers = [
    {
      name: "Tier 1",
      price: 2.99,
      features: ["Full versions of reels from my insta"],
      popular: false,
    },
    {
      name: "Tier 2",
      price: 7,
      features: ["Story times & early releases", "Longer form content on sex & dating", "Erotic audio", "Fan requests"],
      popular: false,
    },
    {
      name: "Tier 3",
      price: 23,
      features: ["My fantasies in detail", "Things I find hot or visualize", "Fan requests"],
      popular: false,
    },
    {
      name: "Spiciest",
      price: 38,
      features: ["Spiciest pics (no full nudity)", "See the most of me", "Message replies", "Fan requests"],
      popular: true,
    },
  ];

  return (
    <section className="section-padding relative" data-testid="patreon-section">
      <div
        className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-20"
        style={{ background: 'radial-gradient(circle, #FF00FF 0%, transparent 70%)' }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-section font-heading text-white mb-4">
            Join My <span className="text-gradient-pink">Inner Circle</span>
          </h2>
          <p className="text-[#FFD1DC] text-lg max-w-2xl mx-auto">
            Get exclusive access to my uncensored content, coaching sessions,
            and a community of like-minded seekers.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              className={`glass-card p-6 relative ${tier.popular ? 'border-[#FF00FF]' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`patreon-tier-${tier.name.toLowerCase().replace(/\s/g, '-')}`}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#FF00FF] rounded-full text-xs font-bold whitespace-nowrap">
                  MOST POPULAR
                </div>
              )}

              <div className="text-center mb-5">
                <h3 className="font-heading text-xl text-white mb-2">{tier.name}</h3>
                <p className="text-3xl font-heading text-[#CCFF00]">
                  ${tier.price}
                  <span className="text-sm text-white/50">/mo</span>
                </p>
              </div>

              <ul className="space-y-2 mb-6 text-sm">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-[#FFD1DC]">
                    <Heart className="w-3 h-3 text-[#FF00FF] mt-1 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="space-y-2">
                <a
                  href={PATREON_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block w-full text-center ${tier.popular ? 'btn-neon' : 'btn-outline'} py-2.5 text-sm`}
                >
                  Join on Patreon
                </a>
                <button
                  onClick={() => setCheckout(tier)}
                  className="block w-full text-center text-white/40 text-xs hover:text-[#FF00FF] py-1 transition-colors"
                >
                  or pay via Razorpay / PayPal
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {checkout && (
        <CheckoutModal
          isOpen={!!checkout}
          onClose={() => setCheckout(null)}
          itemType="patreon"
          itemName={`Acid Rani Patreon - ${checkout.name} ($${checkout.price}/mo)`}
          baseAmount={checkout.price}
          baseCurrency="USD"
        />
      )}
    </section>
  );
};

// Merchandise Section — Muscle Tees Coming Soon
const MerchSection = () => {
  const tees = [
    { name: "Acidrani Signature (Maroon)", image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_275a022e-a8ff-4da2-8fe8-a0f363aabaf6/artifacts/ulzahajs_muscle_tee_acid_rani_back_1x1.jpg" },
    { name: "Acidrani Signature (Black)", image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_275a022e-a8ff-4da2-8fe8-a0f363aabaf6/artifacts/4p5gtdxj_muscle_tee_acidrani_back_1x1.jpg" },
    { name: "Feminist AF", image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_275a022e-a8ff-4da2-8fe8-a0f363aabaf6/artifacts/hx6rj2ax_muscle_tee_feminist_af_front_1x1.jpg" },
    { name: "Acid Smiley", image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_275a022e-a8ff-4da2-8fe8-a0f363aabaf6/artifacts/od4a6psk_muscle_tee_smiley_front_1x1.jpg" },
    { name: "Acidrani x MUSKAN", image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_275a022e-a8ff-4da2-8fe8-a0f363aabaf6/artifacts/xsj0mklw_ChatGPT%20Image%20Aug%2020%2C%202026%2C%2004_12_33%20PM.png" },
  ];

  return (
    <section className="section-padding relative overflow-hidden" data-testid="merch-section">
      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        >
          <h2 className="text-section font-heading text-white mb-3">
            Muscle Tees <span className="text-gradient-pink">Dropping Soon</span>
          </h2>
          <p className="text-[#FFD1DC] text-lg max-w-2xl mx-auto">
            Sleeveless. Unapologetic. Made for bodies that move. Stay tuned.
          </p>
        </motion.div>

        <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory md:grid md:grid-cols-3 lg:grid-cols-5 md:overflow-visible md:pb-0 md:snap-none scrollbar-hide">
          {tees.map((tee, i) => (
            <motion.div
              key={tee.name}
              className="relative rounded-2xl overflow-hidden group flex-shrink-0 w-[200px] md:w-auto snap-start"
              style={{ opacity: 0.65 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 0.65, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              data-testid={`merch-item-${i}`}
            >
              <div className="aspect-[3/4] overflow-hidden">
                <img src={tee.image} alt={tee.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A0510] via-[#1A0510]/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <p className="text-white font-heading text-xs">{tee.name}</p>
                <p className="text-[#FF00FF] text-[10px] font-bold uppercase tracking-wider mt-1">Coming Soon</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p className="text-center text-white/25 text-xs mt-6"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
        >
          Follow @acidrani for drop alerts
        </motion.p>
      </div>
    </section>
  );
};

// Spoil Me Section

// Rave Culture — PLUR Section
const RaveCultureSection = () => {
  const pillars = [
    {
      letter: 'P',
      word: 'Peace',
      color: '#FF69B4',
      description: 'No violence. No aggression. The dancefloor is a sanctuary where every soul finds stillness in the storm of bass.',
    },
    {
      letter: 'L',
      word: 'Love',
      color: '#FF00FF',
      description: 'Love without conditions. Love without labels. Every stranger is a friend you haven\'t danced with yet.',
    },
    {
      letter: 'U',
      word: 'Unity',
      color: '#00FFFF',
      description: 'No discrimination. No exclusion. Every gender, every body, every identity belongs here. Girls rave free — not in fear.',
    },
    {
      letter: 'R',
      word: 'Respect',
      color: '#CCFF00',
      description: 'Consent is not optional. Boundaries are sacred. No harassment. No judging. No bullying. Your body, your rules.',
    },
  ];

  return (
    <section className="section-padding relative overflow-hidden" data-testid="rave-section">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(255,0,255,0.06) 0%, rgba(0,255,255,0.03) 50%, transparent 80%)' }}
      />

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <motion.div className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        >
          <motion.div className="inline-block mb-4"
            animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 3, repeat: Infinity }}
          >
            <Music className="w-10 h-10 mx-auto text-[#FF00FF]" />
          </motion.div>
          <h2 className="text-section font-heading text-white mb-3">
            The <span className="text-gradient-pink">Rave</span> Manifesto
          </h2>
          <p className="text-[#FFD1DC] text-lg max-w-2xl mx-auto">
            Acid Rani believes the dancefloor is the last honest space on earth.
            This is what we stand for. This is how we move.
          </p>
        </motion.div>

        {/* PLUR Letters */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {pillars.map((p, i) => (
            <motion.div
              key={p.word}
              className="p-6 rounded-2xl text-center relative overflow-hidden"
              style={{ background: `${p.color}06`, border: `1px solid ${p.color}20` }}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              data-testid={`plur-${p.word.toLowerCase()}`}
            >
              <p className="font-heading text-6xl mb-2" style={{ color: p.color, textShadow: `0 0 30px ${p.color}40` }}>
                {p.letter}
              </p>
              <h3 className="font-heading text-lg text-white mb-3">{p.word}</h3>
              <p className="text-white/45 text-xs leading-relaxed">{p.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Safe Spaces + Sober Raving */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Safe Spaces for Women */}
          <motion.div
            className="p-6 md:p-8 rounded-2xl"
            style={{ background: 'rgba(255,0,255,0.04)', border: '1px solid rgba(255,0,255,0.15)' }}
            initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          >
            <Users className="w-8 h-8 text-[#FF00FF] mb-3" />
            <h3 className="font-heading text-xl text-white mb-3">Safe Spaces for Women</h3>
            <p className="text-white/45 text-sm leading-relaxed mb-4">
              Acid Rani raves are built around women —
              because the dancefloor should feel like freedom, not a threat. Inclusivity isn't a policy here.
              It's the air we breathe.
            </p>
            <div className="flex flex-wrap gap-2">
              {['No Harassment', 'Consent First', 'Zero Tolerance', 'Safe Exit'].map(tag => (
                <span key={tag} className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                  style={{ background: 'rgba(255,0,255,0.1)', color: '#FF69B4', border: '1px solid rgba(255,0,255,0.2)' }}>
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Sober Raving */}
          <motion.div
            className="p-6 md:p-8 rounded-2xl"
            style={{ background: 'rgba(0,255,255,0.04)', border: '1px solid rgba(0,255,255,0.15)' }}
            initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          >
            <Sparkles className="w-8 h-8 text-[#00FFFF] mb-3" />
            <h3 className="font-heading text-xl text-white mb-3">Sober Raving</h3>
            <p className="text-white/45 text-sm leading-relaxed mb-4">
              You don't need substances to lose yourself in the music. The pressure to do drugs at
              raves is real — and we reject it. Be your authentic self. Feel the bass in your nerves.
              Let your body move because it wants to, not because it's altered.
            </p>
            <div className="flex flex-wrap gap-2">
              {['Feel the Music', 'No Pressure', 'Authentic Self', 'Dance Sober'].map(tag => (
                <span key={tag} className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                  style={{ background: 'rgba(0,255,255,0.1)', color: '#00FFFF', border: '1px solid rgba(0,255,255,0.2)' }}>
                  {tag}
                </span>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom Quote */}
        <motion.div className="text-center mt-12"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
        >
          <p className="font-handwritten text-[#CCFF00] text-lg md:text-xl max-w-lg mx-auto">
            "The dancefloor doesn't ask who you are. It only asks — can you feel it?"
          </p>
          <p className="text-white/25 text-xs mt-3">— Acid Rani</p>
        </motion.div>
      </div>
    </section>
  );
};

// Spoil Me Section
const SpoilMeSection = () => {
  const [customAmount, setCustomAmount] = useState('');
  const [checkout, setCheckout] = useState(null);

  const gifts = [
    {
      icon: MapPin,
      name: "Vacation to Hawaii",
      description: "Send her to paradise. Sun, surf, and a well-deserved escape to the islands.",
      price: 3000,
      color: "#FF69B4",
    },
    {
      icon: Plane,
      name: "Fly Me Home to Canada",
      description: "Help her get back to her roots. A flight home to reconnect with where it all began.",
      price: 1500,
      color: "#00FFFF",
    },
    {
      icon: UtensilsCrossed,
      name: "Luxury Dinner Date",
      description: "Wine, dine, and make her night unforgettable. Only the finest will do.",
      price: 500,
      color: "#CCFF00",
    },
    {
      icon: Camera,
      name: "Sponsor a Photoshoot",
      description: "Fund a professional photoshoot. Your name behind the art she creates.",
      price: 150,
      color: "#FF00FF",
    },
  ];

  return (
    <section className="section-padding relative overflow-hidden" data-testid="spoilme-section">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-15"
        style={{ background: 'radial-gradient(circle, #FF00FF 0%, transparent 70%)' }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <motion.div className="inline-flex items-center gap-3 mb-4"
            initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          >
            <Gift className="w-8 h-8 text-[#FF00FF]" />
            <h2 className="text-section font-heading text-white">
              Spoil <span className="text-gradient-pink">Me</span>
            </h2>
            <Gift className="w-8 h-8 text-[#FF00FF]" />
          </motion.div>
          <p className="text-[#FFD1DC] text-lg max-w-2xl mx-auto mb-2">
            Want to make my day? Pick something from my wishlist or surprise me.
            Every gift gets my personal attention.
          </p>
          <p className="text-[#FF69B4]/60 text-sm max-w-lg mx-auto font-handwritten">
            Write a message or a special request — I read every one personally
          </p>
        </motion.div>

        {/* Gift Cards Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {gifts.map((gift, index) => {
            const Icon = gift.icon;
            return (
              <motion.div
                key={gift.name}
                className="glass-card p-6 relative group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`spoilme-gift-${index}`}
              >
                <motion.div className="mb-4"
                  whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: `${gift.color}15`, border: `1px solid ${gift.color}30` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: gift.color }} />
                  </div>
                </motion.div>

                <h3 className="font-heading text-lg text-white mb-1">{gift.name}</h3>
                <p className="text-white/50 text-xs leading-relaxed mb-4">{gift.description}</p>

                <div className="flex items-center justify-between">
                  <span className="text-[#CCFF00] font-heading text-2xl">${gift.price.toLocaleString()}</span>
                  <button
                    onClick={() => setCheckout(gift)}
                    className="spoil-btn"
                    data-testid={`spoilme-btn-${index}`}
                  >
                    <Heart className="w-4 h-4" />
                    Spoil Her
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Surprise Me Card */}
        <motion.div
          className="glass-card p-8 md:p-10 relative overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          style={{ border: '1px solid rgba(255, 0, 255, 0.3)' }}
          data-testid="spoilme-surprise"
        >
          <motion.div className="absolute top-0 left-0 w-full h-1"
            style={{ background: 'linear-gradient(90deg, transparent, #FF00FF, #CCFF00, #FF00FF, transparent)' }}
            animate={{ x: ['-100%', '100%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          />

          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center gap-3 justify-center md:justify-start mb-3">
                <motion.div animate={{ rotate: [0, 15, -15, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <DollarSign className="w-8 h-8 text-[#CCFF00]" />
                </motion.div>
                <h3 className="font-heading text-2xl md:text-3xl text-white">Surprise Me</h3>
              </div>
              <p className="text-[#FFD1DC] text-base max-w-lg">
                Send a generous amount and get my full attention. The bigger the surprise,
                the bigger my gratitude.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#CCFF00] font-heading text-xl">$</span>
                <input
                  type="number" min="1" placeholder="500"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="surprise-input"
                  data-testid="spoilme-surprise-input"
                />
              </div>
              <button
                onClick={() => setCheckout({ name: 'Surprise Gift', price: Number(customAmount) || 500 })}
                className="btn-neon whitespace-nowrap flex items-center gap-2"
                data-testid="spoilme-surprise-btn"
              >
                <Gift className="w-5 h-5" />
                SEND IT
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      {checkout && (
        <CheckoutModal
          isOpen={!!checkout}
          onClose={() => setCheckout(null)}
          itemType="spoilme"
          itemName={`Acid Rani - ${checkout.name}`}
          baseAmount={checkout.price}
          baseCurrency="USD"
        />
      )}
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="section-padding border-t border-white/10" data-testid="footer">
      <div className="max-w-4xl mx-auto text-center">
        <motion.h2 
          className="text-4xl md:text-6xl font-heading text-white mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Let's <span className="text-gradient-pink">Connect</span>
        </motion.h2>

        <motion.div 
          className="flex flex-wrap gap-4 justify-center items-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="social-link" data-testid="footer-instagram-link">
            <Instagram size={24} />@acidrani
          </a>
          <a href={X_URL} target="_blank" rel="noopener noreferrer" className="social-link" data-testid="footer-x-link">
            <Twitter size={24} />@acidrani
          </a>
          <a href={YOUTUBE_URL} target="_blank" rel="noopener noreferrer" className="social-link" data-testid="footer-youtube-link">
            <Youtube size={24} />YouTube
          </a>
          <a href={PATREON_URL} target="_blank" rel="noopener noreferrer" className="social-link" data-testid="footer-patreon-link">
            <Crown size={24} />Patreon
          </a>
        </motion.div>

        <motion.p 
          className="text-white/40 text-sm"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          © 2024 Acid Rani. All rights reserved. Your body is sovereign territory.
        </motion.p>
      </div>
    </footer>
  );
};

// Main App
function App() {
  const [phase, setPhase] = useState('intro');
  const [showTrackOrder, setShowTrackOrder] = useState(false);

  const handleEnterPortal = useCallback(() => {
    setPhase(p => p === 'intro' ? 'portal' : p);
  }, []);

  const handlePortalComplete = useCallback(() => {
    setPhase(p => p === 'portal' ? 'landing' : p);
  }, []);

  // Check for admin mode via URL param — after all hooks
  const params = new URLSearchParams(window.location.search);
  const adminKey = params.get('admin');
  if (adminKey) {
    return <AdminPanel adminKey={adminKey} />;
  }

  return (
    <>
      <AnimatePresence mode="wait">
        {phase === 'intro' && (
          <IntroPage key="intro" onEnter={handleEnterPortal} />
        )}
        {phase === 'portal' && (
          <PortalTransition key="portal" onComplete={handlePortalComplete} />
        )}
      </AnimatePresence>

      {phase === 'landing' && (
        <motion.div
          className="relative min-h-screen bg-[#1A0510]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          data-testid="main-landing"
        >
          <Scene3D />
          <NoiseOverlay />

          <main className="relative z-10">
            <HeroSection />
            <div id="about"><AboutSection /></div>
            <AcidTabAnimation />
            <div id="patreon"><PatreonSection /></div>
            <div id="coaching"><CoachingSection /></div>
            <div id="merch"><MerchSection /></div>
            <div id="rave"><RaveCultureSection /></div>
            <div id="spoilme"><SpoilMeSection /></div>
            <div id="askme"><AskAcidRaniSection /></div>
            <Footer />
          </main>

          {/* Floating Navigation */}
          <nav className="nav-floating" data-testid="floating-nav">
            <a href="#about" className="nav-link">About</a>
            <a href="#patreon" className="nav-link">Patreon</a>
            <a href="#coaching" className="nav-link">Coaching</a>
            <a href="#merch" className="nav-link">Merch</a>
            <a href="#rave" className="nav-link">Rave</a>
            <a href="#spoilme" className="nav-link">Spoil Me</a>
            <button onClick={() => setShowTrackOrder(true)} className="nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="track-order-nav">
              <Search size={14} className="inline mr-1" />Orders
            </button>
          </nav>

          <TrackOrderModal isOpen={showTrackOrder} onClose={() => setShowTrackOrder(false)} />
        </motion.div>
      )}
    </>
  );
}

export default App;
